import {
    useId,
    HTMLInputTypeAttribute,
    forwardRef,
    useRef,
    useImperativeHandle
} from 'react';
import styles from './ui-form-input.module.scss';
import { Link } from 'react-router';
import { UIText } from '../ui-text';
import { UITextButton } from '../..';

export type UIFormInputProps = {
    name?: string;
    label?: string;
    labelButton?: {
        text: string;
        link?: string;
        onClick?: () => void;
    };
    value?: string;
    placeholder?: string;
    description?: string;
    type?: HTMLInputTypeAttribute;
    required?: boolean;
    disabled?: boolean;
    autoComplete?: 'on' | 'off';
    maxLength?: number;
    errorText?: string;
    errorHighlight?: boolean;
    autoFocus?: boolean;
    onChange?: (value: string) => void;
    onFocusChange?: (isFocused: boolean) => void;
    height?: number;
    style?: React.CSSProperties;
};

export type UIFormInputRef = {
    focus: () => void;
};

export const UIFormInput = forwardRef<UIFormInputRef, UIFormInputProps>(
    (props, ref) => {
        const {
            name,
            label,
            labelButton,
            value,
            placeholder,
            description = '',
            type,
            required = false,
            disabled = false,
            autoComplete,
            maxLength,
            errorText = '',
            errorHighlight = false,
            autoFocus = false,
            onChange,
            onFocusChange,
            height,
            style
        } = props;

        const id = useId();

        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => ({
            focus() {
                inputRef.current?.focus();
            }
        }));

        const inputClasses = [styles.input];
        if (errorText || errorHighlight) {
            inputClasses.push(styles.inputError);
        }
        if (disabled) {
            inputClasses.push(styles.inputDisabled);
        }

        return (
            <div className={styles.container} style={style}>
                {label && (
                    <div className={styles.labelContainer}>
                        <div>
                            <label className={styles.label} htmlFor={id}>
                                {label}
                            </label>
                            {required ? (
                                <label
                                    className={`${styles.label} ${styles.labelRequired}`}
                                    htmlFor={id}
                                >
                                    {' *'}
                                </label>
                            ) : undefined}
                        </div>

                        {labelButton && labelButton.link && (
                            <Link
                                to={labelButton.link}
                                style={{ textDecoration: 'none' }}
                            >
                                <UITextButton variant="primary" type="button">
                                    {labelButton.text}
                                </UITextButton>
                            </Link>
                        )}

                        {labelButton && labelButton.onClick && (
                            <UITextButton
                                onClick={labelButton.onClick}
                                variant="primary"
                                type="button"
                            >
                                {labelButton.text}
                            </UITextButton>
                        )}
                    </div>
                )}
                <input
                    ref={inputRef}
                    className={inputClasses.join(' ')}
                    style={{ height }}
                    type={type}
                    id={id}
                    value={value}
                    name={name}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    aria-invalid={!!errorText}
                    onChange={(e) => {
                        onChange?.(e.target.value);
                    }}
                    autoFocus={autoFocus}
                    onFocus={() => onFocusChange?.(true)}
                    onBlur={() => onFocusChange?.(false)}
                />
                {errorText && (
                    <div className={styles.errorTextContainer}>
                        <UIText variant="small" type="error">
                            {errorText}
                        </UIText>
                    </div>
                )}
                {!errorText && description && (
                    <div className={styles.descriptionTextContainer}>
                        <UIText variant="small" type="default2">
                            {description}
                        </UIText>
                    </div>
                )}
            </div>
        );
    }
);
